import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import {
    useFloating,
    useHover,
    useFocus,
    useClick,
    useInteractions,
    useDismiss,
    safePolygon,
    FloatingPortal,
    offset,
    flip,
    shift,
} from "@floating-ui/react";
import { InfoIcon, PathwayIconWrapperButton } from "../StyledComponents/PathwayIcons";
import stringUtils from "../../_services/string-utils";
import { tooltipSurface } from "../StyledComponents/tooltipSurface";

const FLOATING_MIDDLEWARE = [offset(6), flip(), shift({ padding: 8 })];

const tooltipStyle = (maxWidth) => ({
    ...tooltipSurface(maxWidth),
    padding: "6px 10px",
    zIndex: 9999,
});

export const ControlledPathwayTooltip = (props) => {
    const { refs, floatingStyles, context } = useFloating({
        open: props.isOpen,
        onOpenChange: (open) => props.toggleTooltip(props.id),
        placement: props.placement || "right",
        middleware: FLOATING_MIDDLEWARE,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    return (
        <WrapperButtonForToolTip
            ref={refs.setReference}
            id={props.id}
            data-cy={stringUtils.camelCaseToLowerCaseIdentifier(props.id)}
            style={{ verticalAlign: "middle", width: "25px", position: "relative", top: "-1px" }}
            {...getReferenceProps()}
        >
            {props.icon ? (
                props.icon
            ) : (
                <InfoIcon $primary={props.primaryColor} $size={props.iconSize || "var(--iconSize-s)"} />
            )}
            {props.isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{ ...tooltipStyle(props.maxWidth || "350px"), ...floatingStyles }}
                        {...getFloatingProps()}
                        {...(props.dangerouslySetInnerHTML
                            ? { dangerouslySetInnerHTML: props.dangerouslySetInnerHTML }
                            : {})}
                    >
                        {!props.dangerouslySetInnerHTML && props.children}
                    </div>
                </FloatingPortal>
            )}
        </WrapperButtonForToolTip>
    );
};

ControlledPathwayTooltip.propTypes = {
    id: PropTypes.string.isRequired,
    placement: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    toggleTooltip: PropTypes.func.isRequired,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    primaryColor: PropTypes.bool,
    maxWidth: PropTypes.string,
    iconSize: PropTypes.string,
    dangerouslySetInnerHTML: PropTypes.object,
};

export const PathwayTooltip = (props) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            if (!open && props.closingDelay && isOpen) {
                setTimeout(() => setIsOpen(false), props.closingDelay);
            } else {
                setIsOpen(open);
            }
        },
        placement: props.placement || "right",
        middleware: FLOATING_MIDDLEWARE,
    });

    const hover = useHover(context, { delay: { open: 100, close: 100 }, handleClose: safePolygon() });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss]);

    return (
        <WrapperButtonForToolTip
            ref={refs.setReference}
            data-cy={stringUtils.camelCaseToLowerCaseIdentifier(props.id)}
            style={{ verticalAlign: "middle", width: "25px", position: "relative", top: "-1px" }}
            {...getReferenceProps()}
        >
            {props.icon ? (
                props.icon
            ) : (
                <InfoIcon $primary={props.primaryColor} $size={props.iconSize || "var(--iconSize-s)"} />
            )}
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{ ...tooltipStyle(props.maxWidth || "350px"), ...floatingStyles }}
                        {...getFloatingProps()}
                        {...(props.dangerouslySetInnerHTML
                            ? { dangerouslySetInnerHTML: props.dangerouslySetInnerHTML }
                            : {})}
                    >
                        {!props.dangerouslySetInnerHTML && props.children}
                    </div>
                </FloatingPortal>
            )}
        </WrapperButtonForToolTip>
    );
};

PathwayTooltip.propTypes = {
    id: PropTypes.string.isRequired,
    placement: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    primaryColor: PropTypes.bool,
    maxWidth: PropTypes.string,
    iconSize: PropTypes.string,
    icon: PropTypes.element,
    dangerouslySetInnerHTML: PropTypes.object,
    closingDelay: PropTypes.number,
};

const WrapperButtonForToolTip = styled(PathwayIconWrapperButton)`
    min-height: 0;
    height: auto;
    ${(props) =>
        props.primaryColor &&
        css`
            color: var(--pw-primary);
        `};
`;
